import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, Coins, Gauge } from "lucide-react";

import { adminApi } from "@profitlens/api-client";
import type { AiUsageRowOut } from "@profitlens/api-client";
import { Badge } from "@profitlens/ui/badge";
import { Card } from "@profitlens/ui/card";
import { ErrorState } from "@profitlens/ui/error-state";
import { Input } from "@profitlens/ui/input";
import { Label } from "@profitlens/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@profitlens/ui/select";
import { Spinner } from "@profitlens/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@profitlens/ui/table";

import { KpiCard } from "@/components/kpi-card";
import { PageHeader } from "@/components/page-header";
import { formatDateTime } from "@/lib/format";

const FEATURE_KEYS: Record<string, string> = {
  email: "settings.featureEmail",
  ads_parse: "settings.featureAdsParse",
  title_opt: "settings.featureTitleOpt",
};

/** 本地日期 -> `YYYY-MM-DD`（不能用 toISOString，它会按 UTC 偏移串日期）。 */
function dateInputValue(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

/** 起始日 = 当日 00:00 的绝对时刻（含）。 */
function dayStartIso(date: string): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T00:00:00`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

/** 结束日 = 当日 23:59:59.999 的绝对时刻（含），与后端 `< date_to` 配合不丢当天数据。 */
function dayEndIso(date: string): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function fmtCost(cost: string, currency: string): string {
  const n = Number(cost);
  if (!Number.isFinite(n)) return `${cost} ${currency}`;
  return `${currency} ${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  })}`;
}

/** 预算使用率 >=100% 红、>=80% 黄、其余绿。 */
function budgetTone(pct: number): "success" | "warning" | "danger" {
  if (pct >= 100) return "danger";
  if (pct >= 80) return "warning";
  return "success";
}

function EmptyRow({ colSpan }: { colSpan: number }) {
  const { t } = useTranslation();
  return (
    <TableRow>
      <td colSpan={colSpan} className="py-6 text-center text-sm text-muted">
        {t("aiUsage.empty")}
      </td>
    </TableRow>
  );
}

export function AiUsagePage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState(() => dateInputValue(new Date(Date.now() - 29 * 86_400_000)));
  const [to, setTo] = useState(() => dateInputValue(new Date()));
  const [limit, setLimit] = useState("10");

  const ranking = useQuery({
    queryKey: ["admin", "ai-usage", { from, to, limit }],
    queryFn: () =>
      adminApi.aiUsageRanking({
        from: dayStartIso(from),
        to: dayEndIso(to),
        limit: Number(limit),
      }),
  });

  const featureLabel = (value: string | null | undefined): string =>
    value ? t(FEATURE_KEYS[value] ?? value) : t("aiUsage.empty");

  return (
    <div className="space-y-6">
      <PageHeader title={t("aiUsage.title")} subtitle={t("aiUsage.subtitle")} />

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="ai-usage-from">{t("aiUsage.from")}</Label>
          <Input
            id="ai-usage-from"
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="w-40"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="ai-usage-to">{t("aiUsage.to")}</Label>
          <Input
            id="ai-usage-to"
            type="date"
            value={to}
            min={from}
            onChange={(e) => setTo(e.target.value)}
            className="w-40"
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t("aiUsage.limit")}</Label>
          <Select value={limit} onValueChange={setLimit}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">{t("aiUsage.limitOption", { count: 5 })}</SelectItem>
              <SelectItem value="10">{t("aiUsage.limitOption", { count: 10 })}</SelectItem>
              <SelectItem value="20">{t("aiUsage.limitOption", { count: 20 })}</SelectItem>
              <SelectItem value="50">{t("aiUsage.limitOption", { count: 50 })}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {ranking.isLoading ? (
        <div className="grid place-items-center py-12">
          <Spinner className="h-5 w-5" />
        </div>
      ) : ranking.error ? (
        <ErrorState error={ranking.error} onRetry={() => ranking.refetch()} />
      ) : ranking.data ? (
        <>
          <p className="text-xs text-muted">
            {t("aiUsage.range", {
              from: formatDateTime(ranking.data.date_from),
              to: formatDateTime(ranking.data.date_to),
            })}
          </p>

          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              label={t("aiUsage.totalsCalls")}
              value={ranking.data.totals.calls.toLocaleString()}
              icon={Activity}
            />
            <KpiCard
              label={t("aiUsage.totalsTokens")}
              value={ranking.data.totals.total_tokens.toLocaleString()}
              hint={t("aiUsage.tokensHint", {
                input: ranking.data.totals.input_tokens.toLocaleString(),
                output: ranking.data.totals.output_tokens.toLocaleString(),
              })}
              icon={Gauge}
            />
            <KpiCard
              label={t("aiUsage.totalsCost")}
              value={fmtCost(ranking.data.totals.cost, ranking.data.totals.currency)}
              icon={Coins}
            />
            <KpiCard
              label={t("aiUsage.totalsErrors")}
              value={ranking.data.totals.errors.toLocaleString()}
              tone={ranking.data.totals.errors > 0 ? "negative" : "default"}
              icon={AlertTriangle}
            />
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold">{t("aiUsage.tenantsTitle")}</h2>
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("aiUsage.colTenant")}</TableHead>
                    <TableHead>{t("aiUsage.colCalls")}</TableHead>
                    <TableHead>{t("aiUsage.colTotalTokens")}</TableHead>
                    <TableHead>{t("aiUsage.colCost")}</TableHead>
                    <TableHead>{t("aiUsage.colBudget")}</TableHead>
                    <TableHead>{t("aiUsage.colBudgetUsed")}</TableHead>
                    <TableHead>{t("aiUsage.colRateLimit")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(ranking.data.tenants ?? []).length === 0 ? (
                    <EmptyRow colSpan={7} />
                  ) : (
                    (ranking.data.tenants ?? []).map((row) => (
                      <TenantRow key={row.tenant_id ?? "org"} row={row} />
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold">{t("aiUsage.modelsTitle")}</h2>
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("aiUsage.colModel")}</TableHead>
                    <TableHead>{t("aiUsage.colCalls")}</TableHead>
                    <TableHead>{t("aiUsage.colInputTokens")}</TableHead>
                    <TableHead>{t("aiUsage.colOutputTokens")}</TableHead>
                    <TableHead>{t("aiUsage.colCost")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(ranking.data.models ?? []).length === 0 ? (
                    <EmptyRow colSpan={5} />
                  ) : (
                    (ranking.data.models ?? []).map((row) => (
                      <TableRow key={row.model ?? "unknown"}>
                        <TableCell className="font-mono text-xs">{row.model ?? "—"}</TableCell>
                        <TableCell className="tabular-nums">{row.calls.toLocaleString()}</TableCell>
                        <TableCell className="tabular-nums">
                          {row.input_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {row.output_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {fmtCost(row.cost, t("aiUsage.currencyUsd"))}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </section>

          <section className="space-y-3">
            <h2 className="text-sm font-semibold">{t("aiUsage.featuresTitle")}</h2>
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("aiUsage.colFeature")}</TableHead>
                    <TableHead>{t("aiUsage.colCalls")}</TableHead>
                    <TableHead>{t("aiUsage.colTotalTokens")}</TableHead>
                    <TableHead>{t("aiUsage.colCost")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(ranking.data.features ?? []).length === 0 ? (
                    <EmptyRow colSpan={4} />
                  ) : (
                    (ranking.data.features ?? []).map((row) => (
                      <TableRow key={row.feature ?? "unknown"}>
                        <TableCell>{featureLabel(row.feature)}</TableCell>
                        <TableCell className="tabular-nums">{row.calls.toLocaleString()}</TableCell>
                        <TableCell className="tabular-nums">
                          {row.total_tokens.toLocaleString()}
                        </TableCell>
                        <TableCell className="tabular-nums">
                          {fmtCost(row.cost, t("aiUsage.currencyUsd"))}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </section>
        </>
      ) : null}
    </div>
  );
}

function TenantRow({ row }: { row: AiUsageRowOut }) {
  const { t } = useTranslation();
  const budget = row.budget_usd ?? null;
  const usedPctRaw = row.budget_used_pct ?? null;
  const usedPct = usedPctRaw === null ? null : Number(usedPctRaw);
  const rateLimit = row.rate_limit_per_minute ?? null;

  return (
    <TableRow>
      <TableCell className="font-medium">{row.tenant_name ?? t("aiUsage.tenantUnknown")}</TableCell>
      <TableCell className="tabular-nums">{row.calls.toLocaleString()}</TableCell>
      <TableCell className="tabular-nums">{row.total_tokens.toLocaleString()}</TableCell>
      <TableCell className="tabular-nums">{fmtCost(row.cost, t("aiUsage.currencyUsd"))}</TableCell>
      <TableCell className="tabular-nums">
        {budget === null ? (
          <span className="text-muted">{t("aiUsage.notConfigured")}</span>
        ) : (
          fmtCost(budget, t("aiUsage.currencyUsd"))
        )}
      </TableCell>
      <TableCell className="tabular-nums">
        {usedPct === null ? (
          <span className="text-muted">—</span>
        ) : (
          <Badge variant={budgetTone(usedPct)}>{`${usedPct.toFixed(1)}%`}</Badge>
        )}
      </TableCell>
      <TableCell className="tabular-nums">
        {rateLimit === null ? (
          <span className="text-muted">—</span>
        ) : rateLimit === 0 ? (
          <span className="text-muted">{t("aiUsage.rateLimitOff")}</span>
        ) : (
          t("aiUsage.rateLimitValue", { count: rateLimit })
        )}
      </TableCell>
    </TableRow>
  );
}
